import { SearchOutlined } from '@ant-design/icons';
import { Button, Empty, Grid, Input, List, Modal, Tooltip, Typography, theme } from 'antd';
import React, { KeyboardEventHandler, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { followHref } from '../utils';
import { CommandPaletteItem } from './CommandPalette.utils';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
const SHORTCUT_LABEL = isMac ? '⌘K' : 'Ctrl+K';

export { flattenMenu } from './CommandPalette.utils';
export type { CommandPaletteItem, CommandPaletteOptions } from './CommandPalette.utils';

const COMMAND_PALETTE_OPEN_EVENT = 'decaf:command-palette:open';

interface CommandPaletteProps {
  items: CommandPaletteItem[];
  open: boolean;
  onClose: () => void;
}

function CommandPalette({ items, open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Drop items with no actionable target — selecting them would just close the
  // modal silently. Documented behaviour in CommandPaletteItem.
  const actionable = useMemo(() => items.filter((it) => Boolean(it.to || it.href || it.onSelect)), [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return actionable;
    return actionable.filter((it) => {
      const haystack = [it.label, it.to, it.href, it.group, ...(it.keywords ?? [])]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [actionable, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    const row = scrollRef.current?.querySelector<HTMLElement>(`[data-cp-index="${activeIndex}"]`);
    row?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, filtered]);

  const select = (item: CommandPaletteItem) => {
    onClose();
    if (item.to) {
      navigate(item.to);
    } else if (item.href) {
      followHref(item.href);
    } else if (item.onSelect) {
      item.onSelect();
    }
  };

  const onInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = filtered[activeIndex];
      if (target) select(target);
    }
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} destroyOnHidden width={600} title={null} closable={false}>
      <Input
        autoFocus
        placeholder="Search pages…"
        size="large"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onInputKeyDown}
      />
      <div ref={scrollRef} style={{ marginTop: 12, maxHeight: 360, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <Empty description="No results" />
        ) : (
          <List
            dataSource={filtered}
            renderItem={(item, idx) => (
              <List.Item
                data-cp-index={idx}
                onClick={() => select(item)}
                onMouseEnter={() => setActiveIndex(idx)}
                style={{
                  cursor: 'pointer',
                  padding: '8px 12px',
                  background: idx === activeIndex ? token.controlItemBgActive : 'transparent',
                  borderRadius: token.borderRadius,
                  border: 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                  {item.icon ? <span style={{ display: 'inline-flex' }}>{item.icon}</span> : null}
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.label}
                  </span>
                  <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {[item.group, item.to ?? item.href].filter(Boolean).join(' · ')}
                  </Typography.Text>
                </div>
              </List.Item>
            )}
          />
        )}
      </div>
    </Modal>
  );
}

interface CommandPaletteHostProps {
  items: CommandPaletteItem[];
}

/**
 * Mounts the palette and the global Ctrl-K / Cmd-K hotkey listener.
 *
 * Lives next to the router `<Outlet />` so it survives every navigation.
 * Open state is exposed via `window`-level CustomEvents so the header
 * indicator (rendered separately) can trigger it without prop drilling.
 */
export function CommandPaletteHost({ items }: CommandPaletteHostProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Honor a focused widget that already handled the same shortcut so the
      // global palette stays composable with editors / nested keymaps.
      if (e.defaultPrevented) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    const onOpenEvent = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(COMMAND_PALETTE_OPEN_EVENT, onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(COMMAND_PALETTE_OPEN_EVENT, onOpenEvent);
    };
  }, []);

  return <CommandPalette items={items} open={open} onClose={() => setOpen(false)} />;
}

/**
 * Header pill that opens the palette on click. Hidden below `lg`.
 */
export function CommandPaletteIndicator() {
  const breakpoints = Grid.useBreakpoint();
  if (!breakpoints.lg) return null;

  const open = () => window.dispatchEvent(new CustomEvent(COMMAND_PALETTE_OPEN_EVENT));

  return (
    <Tooltip title={`Search (${SHORTCUT_LABEL})`}>
      <Button
        type="text"
        size="small"
        icon={<SearchOutlined />}
        onClick={open}
        style={{ color: 'rgba(255, 255, 255, 0.5)', marginRight: 12 }}
      >
        {SHORTCUT_LABEL}
      </Button>
    </Tooltip>
  );
}
